/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ClientRow } from '../context/ClientManagementContext';
import { ClientProfileContent } from '../components/ClientProfileContent';
import { ClientDrawer } from '../components/ClientDrawer';
import {
  ClientDetailShell,
  type ClientDetailTab,
} from '../components/ClientDetailView/ClientDetailShell';
import { ClientDetailSkeleton } from '../components/ClientDetailView/ClientDetailSkeleton';
import type { ClientFormSection } from '../components/ClientDetailView/client-quality';
import { readEditSectionParam } from './client-detail-edit-param';
import {
  buildEditDefaultValues,
  fetchClientWithFallback,
  mapResponseToClientRow,
} from './client-detail-mappers';
import { Text, UnstyledButton } from '@/shared/ui';
import { useAuth } from '@/context/authentication';
import { useQueryClient } from '@tanstack/react-query';
import { DealsService } from '@/services/requests/housien-services/DealsService';
import {
  CreateDealDialog,
  type CreateDealFormData,
} from '@/features/deals/components/CreateDealDialog';
import { clientDealsKey } from '../hooks/useClientDeals';

/**
 * Client detail page: loads a client, supports edit, and renders tabs.
 *
 * @returns The rendered client detail page.
 */
export function ClientDetailPage() {
  const { agency_id, client_id } = useParams<{
    agency_id: string;
    client_id: string;
  }>();
  const { t } = useTranslation('features/clients');
  const navigate = useNavigate();
  const auth = useAuth();
  const brokerEmail = (auth.user?.profile as { email?: string })?.email;
  const reactQueryClient = useQueryClient();
  const [client, setClient] = useState<ClientRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  // Whether the in-place create-attendance modal is open. The action used
  // to navigate to `/attendances?clientId=...`, which left the client page
  // behind the attendances list while that page auto-opened its own
  // dialog -- the broker got the modal *and* lost their place. Opening the
  // same dialog here keeps them on the client.
  const [createOpen, setCreateOpen] = useState(false);
  // Which section the edit form should open onto. `null` while it is
  // closed; `undefined` means open it where it normally opens.
  const [editSection, setEditSection] = useState<ClientFormSection | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<ClientDetailTab>('overview');
  const [searchParams, setSearchParams] = useSearchParams();

  // `?edit=<section>` opens the form straight onto that section, so a card
  // elsewhere in the app -- the attendance's "what the client is looking
  // for", say -- can send the broker to the fields it is about instead of
  // dropping them on the profile to find the right accordion themselves.
  // The parameter is consumed on arrival: leaving it in the URL would
  // reopen the drawer on every later render, including right after save.
  const requestedSection = readEditSectionParam(searchParams.get('edit'));
  useEffect(() => {
    if (!requestedSection) return;
    setEditSection(requestedSection);
    setIsEditing(true);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('edit');
      return next;
    }, { replace: true });
  }, [requestedSection, setSearchParams]);

  const loadClient = useCallback(() => {
    if (!agency_id || !client_id) return;
    setIsLoading(true);
    setError(false);
    fetchClientWithFallback(agency_id, client_id)
      .then((c) => setClient(mapResponseToClientRow(c)))
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
  }, [agency_id, client_id]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  // Creates the attendance from the in-place modal, then refreshes the
  // header's open-attendance lookup so the primary action reflects it.
  const handleCreateDeal = useCallback(async (data: CreateDealFormData) => {
    if (!agency_id) return;
    await DealsService.createDeal(agency_id, {
      interest: data.interest,
      broker_id: brokerEmail ?? '',
      client_id: data.client_id || undefined,
      property_id: data.property_id || undefined,
      notes: data.notes || undefined,
    });
    setCreateOpen(false);
    if (client_id) {
      reactQueryClient.invalidateQueries({ queryKey: clientDealsKey(agency_id, client_id) });
    }
  }, [agency_id, client_id, brokerEmail, reactQueryClient]);

  if (!client_id || !agency_id) return null;

  if (isLoading) {
    return <ClientDetailSkeleton />;
  }

  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-(--space-4) p-(--space-12)"
        data-testid="client-detail-error"
      >
        <Text className="text-(--text-secondary) text-sm">
          {t('detail.errorTitle', 'Failed to load client')}
        </Text>
        <UnstyledButton
          type="button"
          onClick={loadClient}
          className="text-sm text-(--seed-blue) hover:underline bg-transparent border-none cursor-pointer"
        >
          {t('detail.retry', 'Try again')}
        </UnstyledButton>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center gap-(--space-4) p-(--space-12)">
        <Text className="text-(--text-secondary) text-sm">
          {t('detail.notFound', 'Client not found')}
        </Text>
        <UnstyledButton
          type="button"
          onClick={() => navigate(`/app/agencies/${agency_id}/clients`)}
          className="text-sm text-(--seed-blue) hover:underline bg-transparent border-none cursor-pointer"
        >
          {t('detail.backToList', 'Back to clients')}
        </UnstyledButton>
      </div>
    );
  }

  return (
    <>
      <ClientDetailShell
        client={client}
        agencyId={agency_id}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onNavigateToList={() => navigate(`/app/agencies/${agency_id}/clients`)}
        onEdit={(section) => {
          setEditSection(section);
          setIsEditing(true);
        }}
        onCreateDeal={() => setCreateOpen(true)}
        onViewAttendance={(dealId) => navigate(`/app/agencies/${agency_id}/deals/${dealId}`)}
      >
        <ClientProfileContent
          client={client}
          agencyId={agency_id}
          activeTab={activeTab}
          onMerged={loadClient}
        />
      </ClientDetailShell>

      <ClientDrawer
        // Re-keyed on the section too: the drawer stays mounted and only
        // resets on the open edge, so a second "Preencher" from another
        // section while it is already open would otherwise keep the first
        // section's expansion.
        key={`${client.client_id}:${editSection ?? 'all'}`}
        open={isEditing}
        focusSection={editSection}
        onClose={() => setIsEditing(false)}
        mode="edit"
        clientId={client.client_id}
        defaultValues={buildEditDefaultValues(client)}
        onSuccess={() => {
          setIsEditing(false);
          loadClient();
        }}
      />

      <CreateDealDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreateDeal}
        agencyId={agency_id}
        initialClientId={client.client_id}
      />
    </>
  );
}

export default ClientDetailPage;
